import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
// Api
import ENV from 'api/env';
import { GET_STREAM_AFFILIATE_LINKS } from 'api/streamAffiliate/queries';
import { SET_STREAM_AFFILIATE_LINK_STATUS } from 'api/streamAffiliate/mutations';
// Types
import {
  SortDirection,
  StreamAffiliateLinkStatus,
  StreamAffiliateLinksOrderBy,
} from 'api/graphql-global-types';
import {
  GetStreamAffiliateLinks,
  GetStreamAffiliateLinksVariables,
  GetStreamAffiliateLinks_getStreamAffiliateLinks_entities,
} from 'api/streamAffiliate/types/GetStreamAffiliateLinks';
import {
  SetStreamAffiliateLinkStatus,
  SetStreamAffiliateLinkStatusVariables,
} from 'api/streamAffiliate/types/SetStreamAffiliateLinkStatus';
// Helpers
import { getEnvLink } from 'helpers/getEnvLink';
import { formatCurrencyString } from 'helpers/utils';
// UI
import Table, {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_NUMBER_ITEMS_PER_PAGE,
} from 'ui/Table';
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './AffiliateStreamLinksModal.module.scss';

type LinkRow = GetStreamAffiliateLinks_getStreamAffiliateLinks_entities;

type LinkItem = {
  id: string;
  affiliate: string;
  email: string;
  isParticipant: boolean;
  url: string;
  status: StreamAffiliateLinkStatus;
  commissionPercent: number;
  clicks: number;
  salesCount: number;
  ticketsSoldCount: number;
  revenue: number;
  commissionEarned: number;
};

type AffiliateStreamLinksModalProps = {
  stream: { id: string; name: string } | null;
  onClose: () => void;
};

type StatusFilter = StreamAffiliateLinkStatus | 'All';

const { Text } = Typography;

const getAffiliateName = (user: LinkRow['link']['user']): string =>
  user.storeDetails?.storeName ||
  `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
  user.slug ||
  '';

const toItems = (entities: LinkRow[]): LinkItem[] =>
  entities.map(({ link, ...totals }) => ({
    id: link.id,
    affiliate: getAffiliateName(link.user),
    email: link.user.email ?? '',
    url: `${getEnvLink(ENV.REACT_APP_ENV)}/a/${link.code}`,
    status: link.status,
    commissionPercent: link.commissionPercent,
    clicks: link.clicks,
    ...totals,
  }));

const copyLink = async (url: string) => {
  try {
    await navigator.clipboard.writeText(url);
    successNotification('Link copied to clipboard');
  } catch (error) {
    errorNotification('Could not copy the link');
  }
};

/** Every affiliate link of one stream, who holds it and what it brought in. */
const AffiliateStreamLinksModal = ({
  stream,
  onClose,
}: AffiliateStreamLinksModalProps): JSX.Element => {
  const [status, setStatus] = useState<StatusFilter>('All');
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    DEFAULT_NUMBER_ITEMS_PER_PAGE
  );
  const [updatingLinkId, setUpdatingLinkId] = useState<string | null>(null);

  const { data, loading } = useQuery<
    GetStreamAffiliateLinks,
    GetStreamAffiliateLinksVariables
  >(GET_STREAM_AFFILIATE_LINKS, {
    variables: {
      input: {
        streamId: stream?.id ?? '',
        status: status === 'All' ? null : status,
        orderBy: StreamAffiliateLinksOrderBy.Commission,
        direction: SortDirection.DESC,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
      },
    },
    skip: !stream,
    fetchPolicy: 'cache-and-network',
  });

  const handleStatusChange = (value: StatusFilter) => {
    setStatus(value);
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  // The streams table counts active links, so it refreshes together with this list.
  const [setLinkStatus] = useMutation<
    SetStreamAffiliateLinkStatus,
    SetStreamAffiliateLinkStatusVariables
  >(SET_STREAM_AFFILIATE_LINK_STATUS, {
    refetchQueries: ['GetStreamAffiliateLinks', 'GetAffiliateStreams'],
    awaitRefetchQueries: true,
  });

  const handleToggleStatus = async (item: LinkItem) => {
    const nextStatus =
      item.status === StreamAffiliateLinkStatus.Active
        ? StreamAffiliateLinkStatus.Disabled
        : StreamAffiliateLinkStatus.Active;

    setUpdatingLinkId(item.id);
    try {
      await setLinkStatus({
        variables: { input: { linkId: item.id, status: nextStatus } },
      });
      successNotification(
        nextStatus === StreamAffiliateLinkStatus.Active
          ? `${item.affiliate}'s link is active again`
          : `${item.affiliate}'s link is disabled`
      );
    } catch (error) {
      errorNotification('Could not change the link status');
    } finally {
      setUpdatingLinkId(null);
    }
  };

  const columns = [
    {
      title: 'Affiliate',
      dataIndex: 'affiliate',
      key: 'affiliate',
      fixed: 'left' as const,
      width: 200,
      render: function Affiliate(name: string, item: LinkItem) {
        return (
          <div>
            <div>{name}</div>
            <Text type="secondary">{item.email}</Text>
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'isParticipant',
      key: 'isParticipant',
      width: 110,
      render: (isParticipant: boolean) =>
        isParticipant ? 'Participant' : 'Affiliate',
    },
    {
      title: 'Link',
      dataIndex: 'url',
      key: 'url',
      width: 300,
      render: function Link(url: string) {
        return (
          <Space>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
            <Button size="small" onClick={() => copyLink(url)}>
              Copy
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: StreamAffiliateLinkStatus) => (
        <Tag
          color={value === StreamAffiliateLinkStatus.Active ? 'green' : 'red'}
        >
          {value}
        </Tag>
      ),
    },
    {
      title: 'Rate',
      dataIndex: 'commissionPercent',
      key: 'commissionPercent',
      align: 'center' as const,
      width: 80,
      render: (percent: number) => `${percent}%`,
    },
    {
      title: 'Clicks',
      dataIndex: 'clicks',
      key: 'clicks',
      align: 'center' as const,
      width: 80,
    },
    {
      title: 'PPV sales',
      dataIndex: 'salesCount',
      key: 'salesCount',
      align: 'center' as const,
      width: 100,
    },
    {
      title: 'Tickets',
      dataIndex: 'ticketsSoldCount',
      key: 'ticketsSoldCount',
      align: 'center' as const,
      width: 90,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      width: 120,
      render: (revenue: number) => formatCurrencyString(revenue),
    },
    {
      title: 'Commission',
      dataIndex: 'commissionEarned',
      key: 'commissionEarned',
      align: 'right' as const,
      width: 120,
      render: (commission: number) => formatCurrencyString(commission),
    },
    {
      title: 'Action',
      dataIndex: 'status',
      key: 'action',
      fixed: 'right' as const,
      width: 110,
      render: function Action(
        value: StreamAffiliateLinkStatus,
        item: LinkItem
      ) {
        const isActive = value === StreamAffiliateLinkStatus.Active;
        const isUpdating = updatingLinkId === item.id;

        return isActive ? (
          <Popconfirm
            title="Disable this link? Sales through it will stop being attributed."
            okText="Disable"
            onConfirm={() => handleToggleStatus(item)}
          >
            <Button size="small" danger loading={isUpdating}>
              Disable
            </Button>
          </Popconfirm>
        ) : (
          <Button
            size="small"
            loading={isUpdating}
            onClick={() => handleToggleStatus(item)}
          >
            Enable
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      visible={Boolean(stream)}
      title={`Affiliate links · ${stream?.name ?? ''}`}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Space className={styles.toolbar}>
        <Text>Status:</Text>
        <Select<StatusFilter>
          value={status}
          onChange={handleStatusChange}
          className={styles.statusSelect}
          options={[
            { value: StreamAffiliateLinkStatus.Active, label: 'Active' },
            { value: StreamAffiliateLinkStatus.Disabled, label: 'Disabled' },
            { value: 'All', label: 'All' },
          ]}
        />
      </Space>

      <Table<LinkItem, string>
        columns={columns}
        data={toItems(data?.getStreamAffiliateLinks?.entities || [])}
        rowKey={(item) => item.id}
        scroll={{ x: 1300 }}
        loading={loading}
        total={data?.getStreamAffiliateLinks?.total}
        currentPage={currentPage}
        pageSize={pageSize}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />
    </Modal>
  );
};

export default AffiliateStreamLinksModal;
